/*
 * FOR SALE — compte Discord du site (clients et équipe), commun à toutes les pages qui portent [data-fs-service].
 * Chargé AVANT fs-propose.js et fs-ticket.js (scripts de module exécutés dans l'ordre du document) : il installe
 * window.FSAccount, qu'ils utilisent pour la session, la connexion et /me.
 *
 * Session : localStorage « fs-session » { token, exp } — valable pour tout le site et entre onglets (événement storage),
 * 7 jours au plus, effacée à la déconnexion et à l'expiration (minuterie). Renouvelée sans bruit (POST /auth/refresh) au
 * plus une fois par heure, au chargement, au retour sur l'onglet et aux appels au service : elle expire après 7 jours sans
 * visite, et 30 jours après la connexion Discord quoi qu'il arrive. Un renouvellement garde la même connexion (`key`,
 * compte et heure de connexion) : aucun « fs:account », les pages ouvertes gardent leur état. Une session d'onglet de
 * l'ancienne version (sessionStorage) est reprise une fois. Connexion : nonce « fs-auth-nonce » et intention « fs-auth-intent »
 * { kind: account | ticket | admin | propose | purchase…, listing?, game?, ticket?, at } dans l'onglet (sessionStorage), puis
 * service → Discord → retour (#fs_code=…, déposé dans « fs-auth-return » par le script en ligne de la page et retiré de
 * l'adresse) → POST /auth/exchange { code, nonce } → session. L'issue est annoncée par l'événement « fs:session »
 * { ok, error, intent } (copie dans window.FSAuthOutcome), puis « fs:account » à chaque changement d'état.
 * Aucun jeton n'est jamais accepté depuis l'adresse.
 * Connexion dans une fenêtre (loginPopup, « Proposer une annonce ») : même départ, mais le retour s'ouvre dans la fenêtre
 * Discord ; le script en ligne fs-account-return y transmet le code à la page d'origine (postMessage de même origine)
 * et la ferme. Cette page garde ainsi tout ce qu'elle a en mémoire (captures non envoyées) et fait l'échange elle-même,
 * avec le nonce de son onglet. Fenêtre bloquée : l'appelant revient à la redirection (login).
 *
 * En-tête ([data-fs-account]) : « Se connecter » ; connecté → initiale, pseudo et marqueur « Équipe », menu « Mes
 * tickets » (GET /tickets/mine), « Mes annonces » (GET /submissions/mine), « Mes achats » (GET /purchases/mine, seulement
 * si la page porte [data-fs-purchase-enabled], réglage fs_direct_purchase), « Se déconnecter ». Les listes s'ouvrent dans
 * un dialogue du cadre FOR SALE, chaque ligne avec l'emblème officiel du jeu (template[data-fs-account-logos]) et un lien
 * vers la page de l'annonce (/pages/listings/<handle>). Tout texte venu du service passe par textContent ; toute adresse
 * est vérifiée. « Mes annonces », annonce en vente (VEND-16, TECH-16) : « Modifier » (?edit=<réf>, fs-propose.js),
 * suivi de la modification, « Demander la suppression » (fenêtre, motif facultatif) et son annulation, « Retirée », « Vendue ».
 * Les fonctions pures sont exportées pour les tests Node. Textes dans la langue de la page (fs-i18n.js, zones account et
 * ticket de i18n/) ; adresses du site préfixées par la langue (path).
 */
import { dialog as uiDialog, dialogHost } from './fs-ui-dialog.js';
import { bindTabs, el as h } from './fs-ui.js';
import { t, has, date, path } from './fs-i18n.js';

export const SESSION_KEY = 'fs-session';
export const NONCE_KEY = 'fs-auth-nonce';
export const RETURN_KEY = 'fs-auth-return';
export const INTENT_KEY = 'fs-auth-intent';
/** Intention de l'ancienne version (fs-ticket.js) : relue une fois, puis effacée. */
export const LEGACY_INTENT_KEY = 'fs-ticket-intent';
/** Durée d'une session sans visite (7 jours) ; le service ne délivre jamais plus long. */
export const SESSION_MAX_MS = 7 * 24 * 60 * 60 * 1000;
/** Plafond absolu après la connexion Discord (30 jours), renouvellements compris. */
export const SESSION_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;
/** Renouvellement : pas avant une heure après le précédent ; après un échec (réseau, Discord), nouvel essai 5 min plus tard. */
export const RENEW_AFTER_MS = 60 * 60 * 1000;
export const RENEW_RETRY_MS = 5 * 60 * 1000;
/** Nom de la fenêtre de connexion : le script de retour la reconnaît à son ouvreur de même origine. */
export const POPUP_NAME = 'fs-discord-login';
export const INTENT_MAX_MS = 10 * 60 * 1000;
/** Jeux connus du service de connexion (modèles d'annonce) ; une autre valeur se connecte avec le premier. */
export const LOGIN_GAMES = Object.freeze(['waruniverse', 'redgalaxy', 'darkorbit', 'novahorizon']);
export const GAME_NAMES = Object.freeze({ waruniverse: 'WarUniverse', redgalaxy: 'Red Galaxy', darkorbit: 'DarkOrbit', novahorizon: 'Nova Horizon' });
/** Intentions de connexion ; « purchase » (achat direct, fs-purchase.js) vise une annonce, « purchases » rouvre « Mes achats ». */
export const INTENT_KINDS = Object.freeze(['account', 'ticket', 'admin', 'propose', 'moderation', 'submissions', 'purchase', 'purchases']);
/** Pages de retour de connexion autres que l'accueil ou une annonce (liste fermée, signée par le service). */
export const RETURN_PAGES = Object.freeze(['moderation', 'contact', 'order-confirmed', 'ventes', 'alerts']);
/** Page de modération de l'équipe (section fs-moderation). */
export const MODERATION_PATH = '/pages/moderation';
/** Ventes en cours de l'équipe (section fs-team-sales, page « ventes » au modèle page.ventes) : refonte de l'espace équipe, lot 1. */
export const SALES_PATH = '/pages/ventes';
/** Alertes et favoris du membre (décision 036), montrés dans le menu quand le service les annonce (/me → features.watch). */
export const WATCH_PATH = '/pages/alerts';
/** Page « Trophée » d'après paiement (fs-order-confirmed.js) ; numéro de commande Shopify (« #1042 ») en paramètre. */
export const TROPHY_PATH = '/pages/order-confirmed';
const ORDER_NAME = /^#?[A-Za-z0-9-]{1,40}$/;
/** Lien d'une ligne de « Mes achats » vers sa page « Trophée » (sans préfixe de langue : passer par path()), sinon ''. */
export const trophyHref = (orderName) => (typeof orderName === 'string' && ORDER_NAME.test(orderName) ? `${TROPHY_PATH}?order=${encodeURIComponent(orderName)}` : '');
export const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
export const TICKET = /^ticket-[0-9a-f]{16}$/;
/** Soumission visée par la page de modération (?listing=<réf>, lien de l'alerte O0E) : gardée dans l'intention de connexion. */
const SUBMISSION_REF = /^\d{1,20}$/;
const TOKEN = /^[A-Za-z0-9._~+/=-]{8,4096}$/;
const CODE = /^[A-Za-z0-9._~-]{8,512}$/;

/** État d'un ticket (dossier) → libellé et ton du badge. */
export const TICKET_STATUS = Object.freeze({
  open: { label: t('ticket.status.open'), tone: 'accent' },
  link_sent: { label: t('ticket.status.link_sent'), tone: 'warn' },
  ordered: { label: t('ticket.status.ordered'), tone: 'warn' },
  paid: { label: t('ticket.status.paid'), tone: 'ok' },
  sold: { label: t('ticket.status.sold'), tone: 'ok' },
  closed: { label: t('ticket.status.closed'), tone: 'muted' },
});
/** Modération d'une annonce proposée → libellé et ton du badge (spec 08 §5). Refus corrigible : « À corriger ». */
export const MODERATION_STATUS = Object.freeze({
  pending: { label: t('account.moderation.pending'), tone: 'warn' },
  approved: { label: t('account.moderation.approved'), tone: 'accent' },
  published: { label: t('account.moderation.published'), tone: 'ok' },
  rejected: { label: t('account.moderation.rejected'), tone: 'danger' },
  fix: { label: t('account.moderation.fix'), tone: 'danger' },
  replaced: { label: t('account.moderation.replaced'), tone: 'muted' },
  /* Annonce publiée sortie de la vitrine (VEND-16, TECH-16) : retirée par l'équipe, ou vendue. */
  withdrawn: { label: t('account.moderation.withdrawn'), tone: 'muted' },
  sold: { label: t('account.moderation.sold'), tone: 'ok' },
});
/** État en vitrine d'une annonce publiée, lu par le service (GET /submissions/mine → listingState). */
export const LISTING_STATES = Object.freeze(['listed', 'hidden', 'sold', 'withdrawn']);
/** Motif facultatif d'une demande de suppression : 300 caractères au plus, lu par l'équipe seulement. */
export const WITHDRAWAL_REASON_MAX = 300;
/** URL de base du service : https (ou http local de développement), sans identifiants, requête ni / final. */
export function serviceBase(raw) {
  try {
    const url = new URL(String(raw || '').trim());
    const local = url.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(url.hostname);
    if ((url.protocol !== 'https:' && !local) || url.username || url.password || url.search || url.hash) return '';
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch { return ''; }
}

/**
 * Charge utile lisible d'un jeton du service (v1.<charge>.<signature>) : { sub, auth?, iat?, exp? } en secondes, ou null.
 * Lecture seulement, sans vérifier la signature : le service reste seul juge de la session.
 */
export function tokenClaims(token) {
  try {
    const part = String(token ?? '').split('.')[1] ?? '';
    if (!part || part.length > 4096 || !/^[A-Za-z0-9_-]+$/.test(part)) return null;
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
    if (!payload || typeof payload !== 'object' || typeof payload.sub !== 'string' || !payload.sub) return null;
    const int = (value) => (Number.isInteger(value) ? value : undefined);
    return { sub: payload.sub, auth: int(payload.auth), iat: int(payload.iat), exp: int(payload.exp) };
  } catch { return null; }
}

/**
 * Identité d'une connexion : compte et heure de connexion Discord, inchangés par un renouvellement (jeton d'avant les
 * sessions longues : exp − 1 h). Jeton illisible : le jeton lui-même.
 */
export function sessionKey(token) {
  const claims = tokenClaims(token);
  if (!claims) return String(token ?? '');
  const login = claims.auth ?? (claims.exp !== undefined ? claims.exp - 3600 : '');
  return `${claims.sub}:${login}`;
}

const withKey = (token, exp) => ({ token, exp, key: sessionKey(token) });

/** Session { token, exp (ms), key } encore valide, 7 jours au plus (marge d'une minute pour l'horloge), sinon null. */
export function readSession(raw, now = Date.now()) {
  try {
    const saved = JSON.parse(raw || 'null');
    if (saved && typeof saved.token === 'string' && TOKEN.test(saved.token) && Number.isFinite(saved.exp)
      && saved.exp > now && saved.exp <= now + SESSION_MAX_MS + 60_000) return withKey(saved.token, saved.exp);
  } catch { /* illisible */ }
  return null;
}

/** Réponse de POST /auth/exchange ou /auth/refresh → session { token, exp, key } (exp en secondes ou millisecondes, plafonné à 7 jours), ou null. */
export function sessionFromExchange(body, now = Date.now()) {
  if (!body || typeof body.token !== 'string' || !TOKEN.test(body.token)) return null;
  const exp = Number.isFinite(body.exp) && body.exp > 0 ? (body.exp < 1e12 ? body.exp * 1000 : body.exp) : now + SESSION_MAX_MS;
  const capped = Math.min(exp, now + SESSION_MAX_MS);
  return capped > now ? withKey(body.token, capped) : null;
}

/**
 * Vrai si la session gagnerait à être renouvelée : jeton du service émis il y a une heure au moins (`iat` ; les jetons
 * d'avant les sessions longues expirent simplement), et au moins une heure de plus à gagner sous le plafond de 30 jours.
 */
export function renewDue(session, now = Date.now()) {
  const claims = tokenClaims(session?.token);
  if (!claims || claims.iat === undefined || !Number.isFinite(session.exp)) return false;
  if (now - claims.iat * 1000 < RENEW_AFTER_MS) return false;
  const login = (claims.auth ?? claims.iat) * 1000;
  return Math.min(now + SESSION_MAX_MS, login + SESSION_LIFETIME_MS) - session.exp >= RENEW_AFTER_MS;
}

/** Intention posée avant la connexion { kind, at, listing?, game?, ticket? }, 10 min au plus, sinon null. */
export function readIntent(raw, now = Date.now()) {
  try {
    const intent = JSON.parse(raw || 'null');
    if (!intent || typeof intent !== 'object' || !INTENT_KINDS.includes(intent.kind) || !Number.isFinite(intent.at)) return null;
    if (intent.at > now + 60_000 || now - intent.at >= INTENT_MAX_MS) return null;
    /* Ticket, équipe et achat visent une annonce : handle obligatoire et valide ; un handle illisible annule toute intention. */
    if (intent.listing !== undefined && (typeof intent.listing !== 'string' || !HANDLE.test(intent.listing))) return null;
    if ((intent.kind === 'ticket' || intent.kind === 'admin' || intent.kind === 'purchase') && !intent.listing) return null;
    return {
      kind: intent.kind, at: intent.at,
      ...(typeof intent.listing === 'string' && HANDLE.test(intent.listing) ? { listing: intent.listing } : {}),
      ...(typeof intent.game === 'string' && HANDLE.test(intent.game) ? { game: intent.game } : {}),
      ...(typeof intent.ticket === 'string' && TICKET.test(intent.ticket) ? { ticket: intent.ticket } : {}),
      ...(intent.kind === 'moderation' && typeof intent.submission === 'string' && SUBMISSION_REF.test(intent.submission) ? { submission: intent.submission } : {}),
    };
  } catch { return null; }
}

export const loginGame = (game) => (LOGIN_GAMES.includes(game) ? game : LOGIN_GAMES[0]);

/** Préfixes d'adresse des langues publiées (/en, /fr, /es, /tr, /ru ; la langue principale n'en a pas). */
export const RETURN_LANGS = Object.freeze(['en', 'fr', 'es', 'tr', 'ru']);
/** Préfixe d'adresse de la page (racine data-fs-root « /fr » → « fr »), '' à la racine « / » ou hors liste. */
export function rootSegment(root) {
  const segment = String(root ?? '').replace(/^\/+|\/+$/g, '');
  return RETURN_LANGS.includes(segment) ? segment : '';
}
/**
 * Départ de la connexion Discord par le service ; la fiche (listing) ou une page connue (returnTo) est rouverte au retour.
 * `lang` : préfixe d'adresse de la page (rootSegment), pour un retour dans la même langue ; absent à la racine « / ».
 */
export function loginUrl(base, { game, nonce, listing, returnTo, lang, switchAccount = false } = {}) {
  const params = new URLSearchParams({ game: loginGame(game), nonce });
  if (listing && HANDLE.test(listing)) params.set('listing', listing);
  else if (RETURN_PAGES.includes(returnTo)) params.set('return', returnTo);
  if (RETURN_LANGS.includes(lang)) params.set('lang', lang);
  // Changer de compte Discord (page « Trophée », ACHAT-22) : le service demande l'écran de Discord avec « Not you? ».
  if (switchAccount === true) params.set('switch', '1');
  return `${base}/auth/discord/start?${params}`;
}

/** Retour de connexion dans le fragment (#fs_code=… / #fs_error=…) ; un « fs_session » n'est que signalé (jamais lu). */
export function parseReturn(hash) {
  const text = String(hash || '');
  const read = (name) => {
    const match = text.match(new RegExp(`(?:^|[#&])${name}=([^&#]*)`));
    if (!match) return null;
    try { return decodeURIComponent(match[1]); } catch { return null; }
  };
  const code = read('fs_code');
  return { present: /(?:^|[#&])fs_(?:code|error|session)=/.test(text), code: code && CODE.test(code) ? code : null, error: read('fs_error') };
}

/**
 * Message d'un retour de connexion refusé (#fs_error=… ou échange en échec). Code refusé par Discord, scope manquant ou
 * Discord injoignable : « n'a pas abouti » ; « expiré » seulement pour une demande expirée ou un code de retour perdu.
 */
export function returnMessage(code) {
  const text = String(code || '').toLowerCase();
  if (/restricted|timeout|pending/.test(text)) return t('account.auth.restricted');
  if (/server_limit/.test(text)) return t('account.auth.server_limit');
  if (/member/.test(text)) return t('account.auth.member');
  if (/denied/.test(text)) return t('account.auth.denied');
  if (/exchange|invalid_code|scope|unavailable/.test(text)) return t('account.auth.failed');
  return t('account.auth.expired');
}

/** Droits lus sur /me → { name, team, publisher, tickets, payments, moderation, pending[, reviews, prices, purchase, sellerTickets, discountCodes] } (blocs du rôle), ou null. */
export function readMe(body) {
  if (!body || typeof body !== 'object' || typeof body.name !== 'string') return null;
  const features = body.features && typeof body.features === 'object' ? body.features : {};
  const publisher = body.publisher === true || body.admin === true;
  const team = publisher || body.staff === true;
  return {
    name: body.name.replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 64) || 'Discord',
    team, publisher,
    tickets: features.tickets === true, payments: features.payments === true,
    /* Modération (spec 08) : réservée à l'équipe ; le service la signale par features.moderation et donne le nombre en attente. */
    moderation: team && features.moderation !== false,
    pending: team && Number.isInteger(body.moderationPending) && body.moderationPending >= 0 ? body.moderationPending : 0,
    /* Avis déposés sur le site (fs-review-form.js, décision 014) : clé présente seulement si le service les annonce. */
    ...(features.reviews === true ? { reviews: true } : {}),
    /* Prix fixé par le vendeur (étape « Votre prix » de fs-propose.js) et achat direct (fs-purchase.js, « Mes achats ») :
       clés présentes seulement si le service les annonce (LISTING_PRICES, DIRECT_PURCHASE). */
    ...(features.prices === true ? { prices: true } : {}),
    ...(features.purchase === true ? { purchase: true } : {}),
    /* Ticket vendeur ouvert par le bot à l'envoi (SELLER_TICKETS, MOD-02) : conditions de vente vente-v6 dans fs-propose.js. */
    ...(features.sellerTickets === true ? { sellerTickets: true } : {}),
    ...(features.supportTickets === true ? { supportTickets: true, supportGames: ['waruniverse', 'redgalaxy', 'darkorbit', 'novahorizon', 'general'].filter(game => Array.isArray(features.supportGames) && features.supportGames.includes(game)) } : {}),
    /* Favoris synchronisés (BUYER_WATCH) et alertes par MP (BUYER_WATCH_DM, comptes pilotes en recette) : fs-watch.js. */
    ...(features.watch === true ? { watch: true, watchAlerts: features.watchAlerts === true } : {}),
    /* Codes de réduction Shopify (DISCOUNT_CODES, espace équipe) : équipe seulement. */
    ...(team && features.discountCodes === true ? { discountCodes: true } : {}),
    /* Jeux qui ont des catégories de tickets (équipe, décision 018) : « Catalogue seul » pour les autres dans l'espace équipe. */
    ...(team && Array.isArray(body.ticketGames) ? { ticketGames: LOGIN_GAMES.filter((game) => body.ticketGames.includes(game)) } : {}),
  };
}

/** Initiale affichée dans le rond du compte (première lettre ou chiffre, majuscule), « ? » sinon. */
export function initialOf(name) {
  const match = String(name || '').normalize('NFKD').match(/[\p{L}\p{N}]/u);
  return match ? match[0].toUpperCase() : '?';
}

/** Fiche d'une annonce sur le site (dialogue de l'accueil, rouvert par l'adresse) ; racine de la langue de la page. */
export const listingHref = (handle, root = path('')) => (HANDLE.test(handle || '') ? `${root}?listing=${encodeURIComponent(handle)}` : '');
/** Page publique d'une annonce (métaobjet « Online store », URL de partage et d'aperçu). */
export const listingPageHref = (handle, origin = '') => (HANDLE.test(handle || '') ? `${origin}/pages/listings/${encodeURIComponent(handle)}` : '');

/** Fil ou salon Discord du serveur, sinon ''. */
export function discordUrl(raw) {
  const text = String(raw || '').trim();
  return /^https:\/\/discord\.com\/channels\/\d{17,20}\/\d{17,20}(?:\/\d{17,20})?$/.test(text) ? text : '';
}

/** Date lisible dans la langue de la page (« 6 oct. 2026 », « Oct 6, 2026 »), '' si illisible. */
export function formatDate(iso, options = { dateStyle: 'medium' }) {
  const time = Date.parse(iso || '');
  if (!Number.isFinite(time)) return '';
  return date(time, options);
}

/** GET /tickets/mine → lignes { ticket, listing, reference, title, game, gameName, status, label, tone, url, openedAt }. */
export function ticketRows(body) {
  const list = Array.isArray(body?.tickets) ? body.tickets : [];
  return list.filter((item) => item && TICKET.test(item.ticket ?? '')).map((item) => {
    const status = TICKET_STATUS[item.status] ? item.status : 'open';
    return {
      ticket: item.ticket, listing: HANDLE.test(item.listing ?? '') ? item.listing : '', reference: String(item.reference ?? '').slice(0, 40),
      title: String(item.title ?? '').slice(0, 160), game: String(item.game ?? ''), gameName: GAME_NAMES[item.game] || String(item.game ?? ''),
      status, label: TICKET_STATUS[status].label, tone: TICKET_STATUS[status].tone, url: discordUrl(item.url), openedAt: item.openedAt ?? '',
    };
  }).sort((a, b) => String(b.openedAt).localeCompare(String(a.openedAt)));
}

export const SUBMISSION_ID = /^[A-Za-z0-9_-]{1,80}$/;
/** « Corriger et renvoyer » : rouvre le parcours prérempli depuis l'annonce refusée (même lien que le MP du bot). */
export const resubmitHref = (id, root = path('')) => (SUBMISSION_ID.test(id || '') ? `${root}?resubmit=${encodeURIComponent(id)}#sell` : '');
/** « Modifier » (VEND-16) : rouvre le parcours prérempli depuis l'annonce en vente, qui reste en ligne jusqu'à la validation. */
export const editHref = (id, root = path('')) => (/^\d{1,20}$/.test(id || '') ? `${root}?edit=${encodeURIComponent(id)}#sell` : '');

const plainText = (value, max) => (typeof value === 'string' ? value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, '').trim().slice(0, max) : '');

const isoOf = (value) => (typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : '');
/**
 * Refus du service (rejection ou decline) → { label, text, advice }, textes bornés. Motif connu par son code (service/
 * moderation.mjs DECLINE_REASONS) : libellé et conseil dans la langue de la page ; sinon ceux du service. Le texte libre
 * de l'équipe (text) reste tel quel.
 */
const refusalOf = (raw, fallback) => {
  const code = typeof raw.code === 'string' && /^[a-z_]{1,40}$/.test(raw.code) ? `account.submissions.reason.code.${raw.code}` : '';
  return {
    label: (code && has(`${code}.label`) ? t(`${code}.label`) : plainText(raw.label, 80)) || fallback,
    text: plainText(raw.text ?? raw.reason, 500),
    advice: code && has(`${code}.advice`) ? t(`${code}.advice`) : code && has(`${code}.label`) ? '' : plainText(raw.advice, 300),
  };
};

/**
 * Dernière modification d'une annonce publiée (`edit` de GET /submissions/mine, VEND-16) → { ref, status, submittedAt,
 * appliedAt, reason, decidedAt, fix } ou null. « Corriger et renvoyer » seulement si le service l'autorise (canResubmit).
 */
export function editOf(raw) {
  if (!raw || typeof raw !== 'object' || !['pending', 'rejected', 'applied'].includes(raw.status)) return null;
  const ref = /^\d{1,20}$/.test(String(raw.ref ?? '')) ? String(raw.ref) : '';
  const rejection = raw.status === 'rejected' && raw.rejection && typeof raw.rejection === 'object' ? raw.rejection : null;
  return {
    ref, status: raw.status, submittedAt: isoOf(raw.submittedAt), appliedAt: raw.status === 'applied' ? isoOf(raw.appliedAt) : '',
    reason: rejection ? refusalOf(rejection, t('account.submissions.reason.edit_fallback')) : null, decidedAt: isoOf(rejection?.at),
    fix: rejection && ref && raw.canResubmit === true ? resubmitHref(ref) : '',
  };
}

/** Demande de suppression du vendeur (`withdrawal`, TECH-16) → { status, at } ou null ; une demande annulée ne se montre plus. */
export function withdrawalOf(raw) {
  if (!raw || typeof raw !== 'object' || !['requested', 'refused', 'done'].includes(raw.status)) return null;
  return { status: raw.status, at: isoOf(raw.at) };
}

/**
 * GET /submissions/mine → lignes { id, title, game, gameName, status, label, tone, submittedAt, decidedAt, listing, line,
 * reason { label, text, advice } | null, fix, replaced, ticketUrl } (spec 08 §5). Une version corrigée passe en tête,
 * l'ancienne juste dessous (« Remplacée », atténuée). `ticketUrl` : ticket vendeur ouvert par le bot (MOD-02), vérifié.
 * Annonce publiée (VEND-16, TECH-16) : `listingState` (vitrine), `edit` (dernière modification), `withdrawal` (demande de
 * suppression), `canEdit` / `editHref` et `canRequestWithdrawal`, tels que le service les donne ; « Retirée » et « Vendue »
 * remplacent « Publiée ».
 */
export function submissionRows(body) {
  const list = Array.isArray(body?.submissions) ? body.submissions : [];
  const rows = list.filter((item) => item && typeof item.title === 'string').map((item) => {
    const status = MODERATION_STATUS[item.moderation] && !['fix', 'replaced'].includes(item.moderation) ? item.moderation : 'pending';
    /* Référence courte du service (ref, contrat 09), sinon l'identifiant. */
    const id = [item.ref, item.id].map((value) => String(value ?? '')).find((value) => SUBMISSION_ID.test(value)) || '';
    const replaced = status === 'rejected' && SUBMISSION_ID.test(String(item.replacedBy ?? ''));
    /* Refus : libellés et conseil TOUJOURS ceux du service (contrat 09), jamais une copie dans le thème. */
    const refusal = item.rejection ?? item.decline;
    const rejection = status === 'rejected' && refusal && typeof refusal === 'object' ? refusal : null;
    /* « Corriger et renvoyer » : seulement si le service l'autorise (motif corrigible, moins de 7 jours, pas encore renvoyée). */
    const fix = status === 'rejected' && !replaced && id && (item.canResubmit ?? item.resubmit) === true ? resubmitHref(id) : '';
    const published = status === 'published';
    const listingState = published && LISTING_STATES.includes(item.listingState) ? item.listingState : '';
    const withdrawal = published ? withdrawalOf(item.withdrawal) : null;
    const gone = listingState === 'withdrawn' || withdrawal?.status === 'done' ? 'withdrawn' : listingState === 'sold' ? 'sold' : '';
    const key = replaced ? 'replaced' : fix ? 'fix' : gone || status;
    const reason = rejection && !replaced ? refusalOf(rejection, t('account.submissions.reason.listing_fallback')) : null;
    const submittedAt = typeof item.submittedAt === 'string' ? item.submittedAt : '';
    const decidedAt = typeof item.publishedAt === 'string' ? item.publishedAt : typeof rejection?.at === 'string' ? rejection.at : '';
    const canEdit = published && !gone && item.canEdit === true && /^\d{1,20}$/.test(id);
    return {
      /* Annonce en vente (VEND-16, TECH-16) : gestes que le service autorise, jamais déduits ici. */
      listingState, gone, edit: published ? editOf(item.edit) : null, withdrawal,
      canEdit, editHref: canEdit ? editHref(id) : '',
      canRequestWithdrawal: published && !gone && item.canRequestWithdrawal === true && withdrawal?.status !== 'requested',
      canCancelWithdrawal: published && !gone && withdrawal?.status === 'requested' && /^\d{1,20}$/.test(id),
      /* Référence publique SITE-… (celle à donner dans le ticket Selling), propre à l'auteur : /submissions/mine ne rend que les siennes. */
      reference: /^SITE-[0-9A-F]{8}$/.test(String(item.reference ?? '')) ? item.reference : '',
      id, title: item.title.slice(0, 160), game: String(item.game ?? ''), gameName: GAME_NAMES[item.game] || String(item.game ?? ''),
      status, key, label: MODERATION_STATUS[key].label, tone: MODERATION_STATUS[key].tone, submittedAt, decidedAt,
      /* Annonce retirée : plus de page à montrer (« Voir l’annonce » disparaît). */
      listing: published && gone !== 'withdrawn' && HANDLE.test(item.listingHandle ?? '') ? item.listingHandle : '',
      replaces: SUBMISSION_ID.test(String(item.replaces ?? '')) ? String(item.replaces) : '',
      replacedAt: replaced && typeof item.replacedAt === 'string' ? item.replacedAt : '',
      reason, fix, replaced,
      /* Ticket vendeur (MOD-02) : la version corrigée porte le même salon, l'ancienne n'en montre plus le lien. */
      ticketUrl: replaced ? '' : discordUrl(item.ticket?.url),
    };
  });
  /* Une version corrigée en tête, l'ancienne (remplacée) juste dessous. */
  const byId = new Map(rows.map((row) => [row.id, row]));
  const placed = new Set();
  const ordered = [];
  for (const row of rows) {
    if (placed.has(row)) continue;
    if (row.replaced && rows.some((other) => other.replaces === row.id)) continue;
    ordered.push(row); placed.add(row);
    const old = row.replaces ? byId.get(row.replaces) : null;
    if (old && !placed.has(old)) { ordered.push(old); placed.add(old); }
  }
  for (const row of rows) if (!placed.has(row)) ordered.push(row);
  return ordered;
}

/** Ligne d'état d'une annonce dans « Mes annonces » (spec 08 §5). */
export function submissionLine(row, format = (iso) => iso) {
  /* Phrase avec ou sans date (clé « _on » : date du service mise en forme par `format`). */
  const dated = (key, iso) => (iso ? t(`account.submissions.line.${key}_on`, { date: format(iso) }) : t(`account.submissions.line.${key}`));
  if (row.key === 'replaced') return dated('replaced', row.replacedAt);
  if (row.status === 'pending') return dated('pending', row.submittedAt);
  if (row.status === 'approved') return t('account.submissions.line.approved');
  if (row.gone === 'withdrawn') return dated('withdrawn', row.withdrawal?.status === 'done' ? row.withdrawal.at : '');
  if (row.gone === 'sold') return t('account.submissions.line.sold');
  if (row.status === 'published') return dated('published', row.decidedAt);
  return '';
}

/**
 * Suivi d'une annonce en vente (VEND-16, TECH-16) : lignes { kind, text } sous la ligne d'état. Modification en relecture,
 * appliquée (« Modifiée le … », date d'application du service ; sans elle « Modification du … appliquée ») ; la refusée a sa boîte de motif (rendu). Demande de suppression en attente
 * ou refusée par l'équipe. Une annonce retirée ou vendue n'a plus de suivi.
 */
export function listingNotes(row, format = (iso) => iso) {
  if (row.status !== 'published' || row.gone) return [];
  const notes = [];
  const note = (key, params) => t(`account.submissions.note.${key}`, params);
  if (row.edit?.status === 'pending') notes.push({ kind: 'edit-pending', text: row.edit.submittedAt ? note('edit_pending_since', { date: format(row.edit.submittedAt) }) : note('edit_pending') });
  if (row.edit?.status === 'applied') {
    notes.push({ kind: 'edit-applied', text: row.edit.appliedAt ? note('edited_on', { date: format(row.edit.appliedAt) })
      : row.edit.submittedAt ? note('edit_applied_of', { date: format(row.edit.submittedAt) }) : note('edit_applied') });
  }
  if (row.withdrawal?.status === 'requested') notes.push({ kind: 'withdrawal-requested', text: row.withdrawal.at ? note('withdrawal_requested_on', { date: format(row.withdrawal.at) }) : note('withdrawal_requested') });
  if (row.withdrawal?.status === 'refused') notes.push({ kind: 'withdrawal-refused', text: note('withdrawal_refused') });
  return notes;
}

/** Motif facultatif d'une demande de suppression : '' si recevable, sinon le message à montrer sous le champ. */
export function withdrawalReasonError(text) {
  return [...String(text ?? '').trim()].length > WITHDRAWAL_REASON_MAX ? t('account.submissions.withdraw.too_long', { max: WITHDRAWAL_REASON_MAX }) : '';
}

/**
 * Réponse refusée d'une demande (ou d'une annulation) de suppression → phrase pour le vendeur, depuis le code d'erreur
 * du service (jamais son texte français) ; repli générique traduit.
 */
export function withdrawalErrorText(status, body) {
  const code = body?.error;
  const error = (key) => t(`account.submissions.withdraw.error.${key}`);
  if (status === 401) return t('account.error.expired');
  if (code === 'listing_sold') return error('listing_sold');
  if (code === 'already_withdrawn') return error('already_withdrawn');
  if (code === 'not_withdrawable') return error('not_withdrawable');
  if (code === 'withdraw_unavailable' || status === 503) return error('unavailable');
  if (status === 422) return error('reason');
  if (status === 429) return error('too_many');
  return t('account.error.service');
}

/**
 * Achat sur le site (dossier, contrat § 3) → libellé et ton du badge de « Mes achats ». « Payé » n'est jamais « remis » :
 * la remise du compte suit le paiement, dans le ticket de livraison.
 */
export const PURCHASE_STATUS = Object.freeze({
  ordered: { label: t('account.purchase.ordered'), tone: 'warn' },
  paid: { label: t('account.purchase.paid'), tone: 'accent' },
  delivered: { label: t('account.purchase.delivered'), tone: 'ok' },
  sold: { label: t('account.purchase.sold'), tone: 'ok' },
  closed: { label: t('account.purchase.closed'), tone: 'muted' },
  refunded: { label: t('account.purchase.refunded'), tone: 'muted' },
});
const PURCHASE_UNKNOWN = Object.freeze({ label: t('account.purchase.unknown'), tone: 'warn' });

/**
 * GET /purchases/mine (tableau, ou { purchases }) → lignes { listing, orderName, status, label, tone, ticketUrl, title,
 * reference, game, gameName, at }, du plus récent au plus ancien. Annonce et ticket vérifiés ; textes bruts (textContent).
 */
export function purchaseRows(body) {
  const list = Array.isArray(body) ? body : Array.isArray(body?.purchases) ? body.purchases : [];
  return list.filter((item) => item && typeof item === 'object').map((item) => {
    const status = typeof item.status === 'string' ? item.status : '';
    const known = Object.hasOwn(PURCHASE_STATUS, status) ? PURCHASE_STATUS[status] : PURCHASE_UNKNOWN;
    const game = typeof item.game === 'string' && HANDLE.test(item.game) ? item.game : '';
    return {
      listing: HANDLE.test(item.listing ?? '') ? item.listing : '', orderName: plainText(item.orderName, 40), status, label: known.label, tone: known.tone,
      ticketUrl: discordUrl(item.ticketUrl), title: plainText(item.title, 160), reference: plainText(item.reference, 40), game, gameName: GAME_NAMES[game] || game,
      at: [item.paidAt, item.createdAt].find((value) => typeof value === 'string' && Number.isFinite(Date.parse(value))) || '',
    };
  }).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

/** Onglets du dialogue « Mon compte » ; « Mes achats » seulement avec l'achat direct (réglage fs_direct_purchase). */
export function accountTabs({ purchases = false } = {}) {
  return [['tickets', t('account.header.tickets')], ['submissions', t('account.header.submissions')], ...(purchases ? [['purchases', t('account.header.purchases')]] : [])];
}

/* ------------------------------------------------------------------ stockage */

const tabStore = {
  get(key) { try { return window.sessionStorage.getItem(key); } catch { return null; } },
  set(key, value) { try { window.sessionStorage.setItem(key, value); return true; } catch { return false; } },
  remove(key) { try { window.sessionStorage.removeItem(key); } catch { /* indisponible */ } },
};
const siteStore = {
  get(key) { try { return window.localStorage.getItem(key); } catch { return tabStore.get(key); } },
  set(key, value) { try { window.localStorage.setItem(key, value); return true; } catch { return tabStore.set(key, value); } },
  remove(key) { try { window.localStorage.removeItem(key); } catch { /* indisponible */ } tabStore.remove(key); },
};

const newNonce = () => (typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, '0')).join(''));
const external = () => h('span', { class: 'fs-visually-hidden', text: ` (${t('account.new_tab')})` });
/** Logo Discord (masque CSS de fs-components.css), décoratif. */
const discordIcon = () => h('span', { class: 'fs-discord-icon', 'aria-hidden': 'true' });
/** Tons d'état du compte et des tickets (ok, warn…) → tons du catalogue (fs-ui-feedback.css, data-tone). */
export const BADGE_TONES = Object.freeze({ ok: 'success', warn: 'warning', danger: 'danger', accent: 'accent', muted: 'neutral' });

/* ------------------------------------------------------------------ compte */

function createAccount(service) {
  let current = null;           // session { token, exp }
  let meToken = '', mePromise = null;
  let expiryTimer = 0;
  let renewing = null, renewAfter = 0;
  const emit = (state, extra = {}) => document.dispatchEvent(new CustomEvent('fs:account', { detail: { state, ...extra } }));

  function load() {
    current = readSession(siteStore.get(SESSION_KEY));
    if (!current) {
      /* Ancienne session d'onglet (sessionStorage) : reprise une fois pour tout le site. */
      const legacy = readSession(tabStore.get(SESSION_KEY));
      tabStore.remove(SESSION_KEY);
      if (legacy) { current = legacy; siteStore.set(SESSION_KEY, JSON.stringify(legacy)); }
      else siteStore.remove(SESSION_KEY);
    }
    arm();
    renew();
    return current;
  }
  function arm() {
    clearTimeout(expiryTimer);
    if (!current) return;
    expiryTimer = setTimeout(() => { if (current && current.exp <= Date.now() + 1000) drop('expired'); }, Math.max(0, current.exp - Date.now()));
  }
  function drop(reason) {
    const had = Boolean(current);
    current = null; meToken = ''; mePromise = null;
    clearTimeout(expiryTimer);
    siteStore.remove(SESSION_KEY);
    if (had) emit('out', { reason });
  }
  function keep(next) {
    current = next; meToken = ''; mePromise = null;
    siteStore.set(SESSION_KEY, JSON.stringify({ token: next.token, exp: next.exp }));
    arm();
    emit('in');
  }

  const session = () => {
    if (current && current.exp <= Date.now()) drop('expired');
    return current;
  };

  /*
   * Renouvellement silencieux : même connexion (key), nouveau jeton et nouvelle échéance, sans « fs:account ». L'ancien
   * jeton reste valable jusqu'à sa propre échéance : une requête déjà partie aboutit. Membre parti ou restreint (403) :
   * session effacée ; 401 : déjà effacée par send ; autre échec : nouvel essai dans 5 min.
   */
  function adopt(next) {
    if (!current || next.key !== current.key) return false;
    if (meToken === current.token) meToken = next.token;
    current = next;
    arm();
    return true;
  }
  function renew() {
    const active = current;
    if (!active || renewing || Date.now() < renewAfter || !renewDue(active)) return;
    renewing = (async () => {
      let status = 0, body = null;
      try { ({ status, body } = await send('/auth/refresh', { method: 'POST' }, active)); } catch { /* réseau */ }
      if (current?.token !== active.token) return;
      const next = status === 200 ? sessionFromExchange(body) : null;
      if (next && next.exp > active.exp && adopt(next)) { siteStore.set(SESSION_KEY, JSON.stringify({ token: next.token, exp: next.exp })); return; }
      if (status === 403) { drop('expired'); return; }
      renewAfter = Date.now() + RENEW_RETRY_MS;
    })().finally(() => { renewing = null; });
  }

  /*
   * Lectures identiques simultanées (même GET, même jeton) : une seule requête, partagée (plusieurs blocs lisent /me,
   * /tickets/mine, /features… au même moment). S'il y a eu partage, chaque appelant reçoit sa propre copie du corps.
   * Jamais pour une écriture ; toute écriture oublie les lectures en cours à son départ et à son retour, et aucune lecture
   * n'est partagée tant qu'une écriture est en cours : une lecture demandée pendant ou après une écriture part d'elle-même.
   * `timeoutMs` (écritures) : délai avant d'abandonner l'attente (20 s par défaut) ; le service, lui, continue.
   * `signal` : annulation par l'appelant (aperçu remplacé par une saisie plus récente) ; la réponse est alors { status: 0 }.
   */
  const reads = new Map();
  let writing = 0;
  const copy = (value) => (value !== null && typeof value === 'object' ? structuredClone(value) : value);
  async function call(path, { method = 'GET', body, form, keepalive = false, timeoutMs = 20000, signal = null } = {}) {
    const active = session();
    renew();
    if (method !== 'GET' || body || form || keepalive) {
      reads.clear(); writing++;
      return send(path, { method, body, form, keepalive, timeoutMs, signal }, active).finally(() => { writing--; reads.clear(); });
    }
    if (writing) return send(path, { method, keepalive }, active);
    const key = `${active?.token ?? ''} ${path}`;
    let entry = reads.get(key);
    if (!entry) {
      entry = { callers: 0, promise: null };
      const own = entry;
      own.promise = send(path, { method, keepalive }, active).finally(() => { if (reads.get(key) === own) reads.delete(key); });
      reads.set(key, own);
    }
    entry.callers++;
    const result = await entry.promise;
    return entry.callers > 1 ? { ...result, body: copy(result.body) } : result;
  }
  async function send(path, { method, body, form, keepalive, timeoutMs = 20000, signal = null }, active) {
    // Signal déjà annulé : rien n'est armé (aucun minuteur laissé vivant).
    if (signal?.aborted) return { status: 0, body: null, headers: null };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const cancel = () => controller.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    try {
      /* Sans `cache: 'no-store'` : Chromium ignorait alors son cache de pré-vérification CORS (une OPTIONS avant chaque
         appel signé, 38 % du trafic du service en octobre 2026). Le service répond déjà `Cache-Control: no-store`, sauf
         /features (public, 5 min, voulu). */
      const response = await fetch(`${service}${path}`, {
        method, mode: 'cors', credentials: 'omit', signal: controller.signal, keepalive,
        headers: { ...(active ? { Authorization: `Bearer ${active.token}` } : {}), ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : form ? { body: form } : {}),
      });
      let data = null;
      try { data = await response.json(); } catch { /* corps vide */ }
      /* Jeton refusé : la session est effacée pour tout le site. */
      if (response.status === 401 && active && current?.token === active.token) drop('expired');
      return { status: response.status, body: data, headers: response.headers };
    } catch (error) {
      if (signal?.aborted) return { status: 0, body: null, headers: null };
      throw error;
    } finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
  }

  /** Droits du compte, lus une fois par session. Discord ou service injoignable : null (aucun bloc réservé). */
  function me() {
    const active = session();
    if (!active) return Promise.resolve(null);
    if (mePromise && meToken === active.token) return mePromise;
    meToken = active.token;
    mePromise = call('/me').then(({ status, body }) => (status === 200 ? readMe(body) : null), () => { mePromise = null; return null; });
    return mePromise;
  }

  /** Prépare la connexion (nonce et intention de l'onglet) et rend l'adresse de départ ; '' si l'onglet ne stocke rien. */
  function prepareLogin({ kind = 'account', game, listing, ticket, submission, returnTo, switchAccount = false } = {}) {
    const nonce = newNonce();
    const intent = { kind: INTENT_KINDS.includes(kind) ? kind : 'account', at: Date.now(),
      ...(listing && HANDLE.test(listing) ? { listing } : {}), ...(game && HANDLE.test(game) ? { game } : {}), ...(ticket && TICKET.test(ticket) ? { ticket } : {}),
      ...(kind === 'moderation' && typeof submission === 'string' && SUBMISSION_REF.test(submission) ? { submission } : {}) };
    if (!tabStore.set(NONCE_KEY, nonce) || !tabStore.set(INTENT_KEY, JSON.stringify(intent))) return '';
    return loginUrl(service, { game, nonce, listing, returnTo, switchAccount, lang: rootSegment(document.documentElement.dataset.fsRoot) });
  }
  function login(options = {}) {
    const url = prepareLogin(options);
    if (url) window.location.assign(url);
    return Boolean(url);
  }
  function logout() { drop('logout'); }

  /** Échange d'un code de retour contre la session, avec le nonce de cet onglet : { ok, error }. */
  async function exchangeCode(code, nonce) {
    if (!code || !CODE.test(code) || !nonce) return { ok: false, error: 'expired' };
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      const response = await fetch(`${service}/auth/exchange`, { method: 'POST', mode: 'cors', credentials: 'omit', signal: controller.signal,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, nonce }) }).finally(() => clearTimeout(timer));
      const body = await response.json().catch(() => null);
      const next = response.status === 200 ? sessionFromExchange(body) : null;
      /* 400 : code rejoué, expiré ou nonce différent (« connexion expirée ») ; autre échec : service ou Discord. */
      if (!next) return { ok: false, error: response.status === 400 ? 'exchange_refused' : 'exchange_failed' };
      keep(next);
      return { ok: true, error: null };
    } catch { return { ok: false, error: 'exchange_failed' }; }
  }

  /**
   * Connexion dans une fenêtre séparée : { blocked: true, url } si le navigateur refuse la fenêtre (l'appelant peut
   * alors rediriger vers url, nonce et intention déjà posés), sinon { blocked: false, promise } ; la promesse rend
   * { ok, error } (error « closed » si la fenêtre est fermée sans retour). À appeler pendant le clic du visiteur.
   */
  function loginPopup(options = {}) {
    const url = prepareLogin(options);
    if (!url) return { blocked: false, promise: Promise.resolve({ ok: false, error: 'storage' }) };
    let popup = null;
    try { popup = window.open(url, POPUP_NAME, 'popup=yes,width=520,height=760'); } catch { popup = null; }
    if (!popup) return { blocked: true, url };
    const promise = new Promise((resolve) => {
      let done = false;
      let timer = 0;
      const finish = (outcome) => {
        if (done) return;
        done = true;
        clearInterval(timer);
        window.removeEventListener('message', onMessage);
        resolve(outcome);
      };
      async function onMessage(event) {
        if (event.origin !== window.location.origin || event.source !== popup) return;
        const back = event.data && typeof event.data === 'object' ? event.data.fsAuthReturn : null;
        if (!back || typeof back !== 'object') return;
        clearInterval(timer);
        window.removeEventListener('message', onMessage);
        const nonce = tabStore.get(NONCE_KEY);
        tabStore.remove(NONCE_KEY);
        tabStore.remove(INTENT_KEY);
        if (typeof back.code !== 'string') { finish({ ok: false, error: typeof back.error === 'string' ? back.error.slice(0, 80) : 'expired' }); return; }
        finish(await exchangeCode(back.code, nonce));
      }
      window.addEventListener('message', onMessage);
      timer = setInterval(() => { if (popup.closed) finish({ ok: false, error: 'closed' }); }, 600);
    });
    return { blocked: false, promise };
  }

  /* ---- retour de Discord : code déposé par le script en ligne de la page (adresse déjà nettoyée), ou lu ici ---- */
  let returning = null;
  (() => {
    let back = null;
    try {
      const early = JSON.parse(tabStore.get(RETURN_KEY) || 'null');
      if (early && typeof early === 'object') back = { present: true, code: typeof early.code === 'string' && CODE.test(early.code) ? early.code : null, error: typeof early.error === 'string' ? early.error : null };
    } catch { /* illisible */ }
    tabStore.remove(RETURN_KEY);
    if (!back) back = parseReturn(window.location.hash);
    if (/(?:^|[#&])fs_(?:code|error|session)=/.test(window.location.hash)) {
      try { history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`); } catch { /* adresse inchangée */ }
    }
    if (!back.present) return;
    const intent = readIntent(tabStore.get(INTENT_KEY)) || legacyIntent();
    tabStore.remove(INTENT_KEY);
    const nonce = tabStore.get(NONCE_KEY);
    tabStore.remove(NONCE_KEY);
    const announce = (outcome) => {
      const detail = { ...outcome, intent };
      window.FSAuthOutcome = Object.freeze({ ...detail, at: Date.now() });
      document.dispatchEvent(new CustomEvent('fs:session', { detail }));
      return detail;
    };
    const promise = (async () => {
      if (!back.code || !nonce) return announce({ ok: false, error: back.error || 'expired' });
      return announce(await exchangeCode(back.code, nonce));
    })();
    returning = { intent, promise };
  })();

  function legacyIntent() {
    try {
      const old = JSON.parse(tabStore.get(LEGACY_INTENT_KEY) || 'null');
      tabStore.remove(LEGACY_INTENT_KEY);
      return old ? readIntent(JSON.stringify(old)) : null;
    } catch { return null; }
  }

  /* Autre onglet : connexion ou déconnexion répercutée ici. Déconnexion : tout de suite (rien de privé ne reste affiché).
     Connexion vue par un onglet caché : annoncée seulement quand il redevient visible ; chaque onglet ouvert relisait
     sinon /me, /tickets/mine et /features au même instant, et la rafale dépassait la limite par IP du service (429,
     achat compris). */
  let pendingIn = false;
  function syncFromStore() {
    const next = readSession(siteStore.get(SESSION_KEY));
    if (next?.token === current?.token) return;
    /* Renouvellement fait par un autre onglet : même connexion, rien à annoncer. */
    if (next && adopt(next)) return;
    current = next; meToken = ''; mePromise = null;
    arm();
    if (next && document.hidden) { pendingIn = true; return; }
    pendingIn = false;
    emit(next ? 'in' : 'out', { reason: 'other-tab' });
  }
  window.addEventListener('storage', (event) => {
    if (event.key === SESSION_KEY || event.key === null) syncFromStore();
  });
  /* Page restaurée du cache de navigation (Précédent, Suivant) : aucun événement storage n'y est remis pendant
     l'absence. Connexion ou déconnexion faite ailleurs entre-temps : relue ici, annoncée comme celle d'un autre onglet. */
  window.addEventListener('pageshow', (event) => { if (event.persisted) syncFromStore(); });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) renew();
    if (!pendingIn || document.hidden) return;
    pendingIn = false;
    if (session()) emit('in', { reason: 'other-tab' });
  });

  if (!returning) load();
  else { current = readSession(siteStore.get(SESSION_KEY)); arm(); renew(); }
  return Object.freeze({ service, session, call, me, prepareLogin, login, loginPopup, logout, get returning() { return returning; } });
}

/* ------------------------------------------------------------------ en-tête et listes */

function currentGame() {
  const root = document.querySelector('[data-fs-universe-root]') || document.querySelector('[data-fs-account-root]');
  const game = root?.getAttribute('data-fs-universe') || '';
  return LOGIN_GAMES.includes(game) ? game : '';
}
function openListing() {
  try { const value = (new URLSearchParams(window.location.search).get('submission') ?? new URLSearchParams(window.location.search).get('listing') ?? new URLSearchParams(window.location.search).get('annonce')) || ''; return HANDLE.test(value) ? value : ''; } catch { return ''; }
}

function mountHeader(account, slot) {
  if (slot.dataset.fsAccountReady) return;
  slot.dataset.fsAccountReady = 'true';
  const id = `fs-account-menu-${Math.random().toString(36).slice(2, 8)}`;
  let menuOpen = false;
  let rights = null;
  /* Achat direct (réglage fs_direct_purchase de la section) : « Mes achats » dans le menu et le dialogue. Sinon, menu d'aujourd'hui. */
  const purchasesOn = Boolean(document.querySelector('[data-fs-purchase-enabled]'));

  const loginButton = h('button', { type: 'button', class: 'fs-btn fs-account-login', 'data-fs-account-login': true });
  loginButton.append(discordIcon(), h('span', { class: 'fs-account-login-label', text: t('account.header.login') }));
  loginButton.addEventListener('click', () => {
    const listing = document.querySelector('[data-fs-account-root] [data-fs-ld]')?.getAttribute('data-fs-handle') || openListing();
    if (!account.login({ kind: 'account', game: currentGame(), listing })) loginButton.title = t('account.error.storage');
  });

  const avatar = h('span', { class: 'fs-account-avatar', 'aria-hidden': 'true' });
  const name = h('span', { class: 'fs-account-name' });
  const team = h('span', { class: 'fs-badge fs-account-team', 'data-tone': 'accent', text: t('account.header.team'), hidden: true });
  const toggle = h('button', { type: 'button', class: 'fs-btn fs-account-toggle', 'aria-expanded': 'false', 'aria-controls': id, 'data-fs-account-toggle': true },
    avatar, h('span', { class: 'fs-account-who' }, name, team), h('span', { class: 'fs-account-caret', 'aria-hidden': 'true' }));
  const menuItem = (label, action, extra = {}) => h('li', {}, h('button', { type: 'button', class: 'fs-account-item', onclick: () => { close(); action(); }, ...extra }, label));
  const menuHead = h('p', { class: 'fs-account-menu-head' });
  const menu = h('div', { class: 'fs-account-menu', id, hidden: true, 'data-fs-account-menu': true },
    menuHead,
    h('ul', { class: 'fs-account-items' },
      menuItem(t('account.header.tickets'), () => openPanel('tickets'), { 'data-fs-account-open': 'tickets' }),
      menuItem(t('account.header.submissions'), () => openPanel('submissions'), { 'data-fs-account-open': 'submissions' }),
      purchasesOn ? menuItem(t('account.header.purchases'), () => openPanel('purchases'), { 'data-fs-account-open': 'purchases' }) : null,
      h('li', { hidden: true, 'data-fs-account-watch-only': true }, h('a', { class: 'fs-account-item', href: path(WATCH_PATH), 'data-fs-account-watch': true }, t('account.header.watch'))),
      h('li', { hidden: true, 'data-fs-account-team-only': true }, h('a', { class: 'fs-account-item', href: path(SALES_PATH), 'data-fs-account-sales': true }, t('account.header.team_sales'))),
      h('li', { hidden: true, 'data-fs-account-team-only': true }, h('a', { class: 'fs-account-item', href: path(MODERATION_PATH), 'data-fs-account-moderation': true }, t('account.header.team_space'), h('span', { class: 'fs-counter fs-account-pending', 'aria-hidden': 'true', 'data-fs-account-pending': true }), h('span', { class: 'fs-visually-hidden', 'data-fs-account-pending-text': true }))),
      menuItem(t('account.header.logout'), () => { account.logout(); loginButton.focus({ preventScroll: true }); }, { 'data-fs-account-logout': true })));
  const signed = h('div', { class: 'fs-account-signed', hidden: true }, toggle, menu);
  slot.replaceChildren(loginButton, signed);
  slot.hidden = false;

  function close() { if (!menuOpen) return; menuOpen = false; menu.hidden = true; toggle.setAttribute('aria-expanded', 'false'); }
  function open() { menuOpen = true; menu.hidden = false; toggle.setAttribute('aria-expanded', 'true'); menu.querySelector('.fs-account-item')?.focus({ preventScroll: true }); }
  toggle.addEventListener('click', () => (menuOpen ? close() : open()));
  document.addEventListener('click', (event) => { if (menuOpen && !slot.contains(event.target)) close(); });
  slot.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menuOpen) { event.preventDefault(); close(); toggle.focus({ preventScroll: true }); return; }
    if (!menuOpen || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [...menu.querySelectorAll('button, a')].filter((item) => !item.closest('[hidden]'));
    const index = items.indexOf(document.activeElement);
    const next = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: items.length - 1 }[event.key];
    event.preventDefault();
    items[(next + items.length) % items.length]?.focus({ preventScroll: true });
  });
  /* Focus parti ailleurs (Tab) : fermeture. Sans destination (relatedTarget nul), rien : Safari et Chrome sur iPhone ne
     donnent pas le focus au bouton touché, le menu se refermait avant le clic et aucun choix n'aboutissait. Un toucher
     ou un clic hors du menu le ferme (écouteur du document ci-dessus). Même garde que le choix de langue (fs-lang.js). */
  slot.addEventListener('focusout', (event) => { if (menuOpen && event.relatedTarget && !slot.contains(event.relatedTarget)) close(); });

  async function render() {
    const active = account.session();
    loginButton.hidden = Boolean(active);
    signed.hidden = !active;
    slot.toggleAttribute('data-fs-signed-in', Boolean(active));
    if (!active) { close(); rights = null; team.hidden = true; return; }
    rights = await account.me();
    if (!account.session()) return render();
    const label = rights?.name || t('account.header.fallback_name');
    avatar.textContent = initialOf(label);
    name.textContent = label;
    team.hidden = !rights?.team;
    menu.querySelectorAll('[data-fs-account-team-only]').forEach((item) => { item.hidden = !rights?.team; });
    menu.querySelectorAll('[data-fs-account-watch-only]').forEach((item) => { item.hidden = !rights?.watch; });
    const pending = menu.querySelector('[data-fs-account-pending]');
    if (pending) pending.textContent = rights?.pending ? String(rights.pending) : '';
    const pendingText = menu.querySelector('[data-fs-account-pending-text]');
    if (pendingText) pendingText.textContent = rights?.pending ? ` (${t('account.header.pending', { count: rights.pending })})` : '';
    toggle.setAttribute('aria-label', t(rights?.team ? 'account.header.toggle_team' : 'account.header.toggle', { name: label }));
    menuHead.replaceChildren(discordIcon(), t('account.header.signed_in'), rights?.team ? h('span', { class: 'fs-account-menu-role', text: ` · ${t('account.header.role')}` }) : '');
  }

  /* ---- listes « Mes tickets » / « Mes annonces » dans le dialogue du catalogue (fs-ui-dialog.js), cadre de référence ---- */
  let dialog = null;
  function ensureDialog() {
    if (dialog) return dialog;
    const tabs = h('div', { class: 'fs-tabs fs-account-tabs', role: 'tablist', 'aria-label': t('account.panel.tabs') });
    const box = uiDialog({ eyebrow: t('account.panel.eyebrow'), close: t('account.panel.close'), head: [tabs], remove: false, attrs: { 'data-fs-account-panel': true },
      bodyAttrs: { role: 'tabpanel', 'aria-live': 'polite' } });
    dialog = { node: box.node, title: box.title, tabs, body: box.body, box, kind: '' };
    return dialog;
  }
  /* Ouvert depuis le menu (qui se referme) : le focus revient au bouton du compte. */
  const showPanel = (view) => view.box.open({ host: dialogHost(slot), opener: signed.hidden ? null : toggle });
  /* Lien qui quitte le panneau (annonce, formulaire, ancre) : fermeture sans reprendre le focus. */
  const leavePanel = () => { dialog?.box.setOpener(null); dialog?.box.close(); };

  /* Badge d'état du catalogue (fs-ui-feedback.css) : tons du compte (ok, warn…) relus en tons du catalogue. */
  function badge(text, tone) { return h('span', { class: 'fs-badge fs-row-aside', 'data-tone': BADGE_TONES[tone] || null, text }); }
  /* Emblème officiel du jeu (template[data-fs-account-logos] de l'en-tête), décoratif : le nom du jeu est écrit à côté. */
  function gameMark(game) {
    const logo = document.querySelector('template[data-fs-account-logos]')?.content.querySelector(`[data-fs-logo="${CSS.escape(game || '')}"]`);
    const mark = h('span', { class: 'fs-row-media fs-account-game', 'aria-hidden': 'true' });
    if (logo) mark.append(logo.cloneNode(true));
    else mark.textContent = initialOf(GAME_NAMES[game] || game);
    return mark;
  }
  /* Annonce refusée : « Motif : {libellé}. {texte}. {conseil} » en entier (texte seul), « Corriger et renvoyer » si permis.
     Modification refusée (VEND-16) : même boîte, titrée « Modification refusée », l'annonce reste en ligne telle qu'elle était. */
  function rejected(row, { heading = t('account.submissions.reason.heading'), keep = t('account.submissions.reason.keep') } = {}) {
    const fix = row.fix ? h('a', { class: 'fs-btn fs-btn--primary', href: path(row.fix), 'data-fs-account-fix': row.id, onclick: leavePanel }, t('account.submissions.reason.fix')) : null;
    return h('div', { class: 'fs-account-reason' },
      h('p', { class: 'fs-account-reason-title' }, t('account.submissions.reason.title', { heading, label: row.reason.label }), row.decidedAt ? h('span', { class: 'fs-account-reason-date', text: ` · ${formatDate(row.decidedAt)}` }) : ''),
      row.reason.text ? h('p', { class: 'fs-account-reason-text', text: row.reason.text }) : null,
      row.reason.advice ? h('p', { class: 'fs-account-reason-advice' }, h('strong', { text: t('account.submissions.reason.advice') }), row.reason.advice) : null,
      row.fix ? h('p', { class: 'fs-account-reason-keep', text: keep }) : null,
      h('p', { class: 'fs-account-reason-sign', text: t('account.submissions.reason.sign') }),
      fix ? h('p', { class: 'fs-account-row-actions' }, fix) : null);
  }
  function editRejected(row) {
    return rejected({ id: row.edit.ref, reason: row.edit.reason, fix: row.edit.fix, decidedAt: row.edit.decidedAt }, { heading: t('account.submissions.reason.edit_fallback'),
      keep: t('account.submissions.reason.keep_edit') });
  }

  /* « Demander la suppression » (TECH-16) : petite fenêtre de confirmation au-dessus de « Mes annonces », motif facultatif
     (300 caractères, lu par l'équipe seulement). L'annonce reste en ligne jusqu'à la décision de l'équipe. */
  function askWithdrawal(row, opener) {
    const id = `${dialog?.node.getAttribute('aria-labelledby') || 'fs-account'}-withdraw`;
    const reason = h('textarea', { class: 'fs-textarea fs-account-confirm-reason', id: `${id}-reason`, rows: '3', maxlength: String(WITHDRAWAL_REASON_MAX + 20), 'aria-describedby': `${id}-hint ${id}-error` });
    const count = h('span', { class: 'fs-account-confirm-count', 'aria-hidden': 'true', text: `0 / ${WITHDRAWAL_REASON_MAX}` });
    const error = h('p', { class: 'fs-account-confirm-error', id: `${id}-error`, role: 'alert' });
    const send = h('button', { type: 'button', class: 'fs-btn fs-btn--danger', 'data-fs-account-withdraw-confirm': row.id }, t('account.submissions.withdraw.ask'));
    const cancel = h('button', { type: 'button', class: 'fs-btn fs-btn--link', 'data-fs-account-withdraw-cancel-dialog': true }, t('account.submissions.withdraw.cancel'));
    /* Confirmation du catalogue, empilée sur « Mon compte » : Échap et le fond ne ferment qu'elle, focus rendu au bouton. */
    const box = uiDialog({
      title: t('account.submissions.withdraw.ask'), lead: t('account.submissions.withdraw.lead'), size: 'sm', alert: true, close: '',
      attrs: { 'data-fs-account-withdraw-dialog': row.id },
      head: [h('p', { class: 'fs-dialog-meta' }, row.reference ? h('span', { class: 'fs-account-ref', text: row.reference }) : null, row.reference ? ' · ' : '', row.title)],
      body: [h('div', { class: 'fs-account-confirm-field' },
        h('label', { class: 'fs-account-confirm-label', for: `${id}-reason` }, `${t('account.submissions.withdraw.reason')} `, h('span', { class: 'fs-account-confirm-optional', text: t('account.submissions.withdraw.optional') })),
        reason,
        h('p', { class: 'fs-account-confirm-hint', id: `${id}-hint` }, h('span', { text: t('account.submissions.withdraw.hint') }), count),
        error)],
      actions: [cancel, send],
    });
    const node = box.node;
    reason.addEventListener('input', () => {
      count.textContent = `${[...reason.value.trim()].length} / ${WITHDRAWAL_REASON_MAX}`;
      error.textContent = '';
      reason.removeAttribute('aria-invalid');
    });
    cancel.addEventListener('click', () => box.close('cancel'));
    send.addEventListener('click', async () => {
      if (send.getAttribute('aria-busy') === 'true') return;
      const invalid = withdrawalReasonError(reason.value);
      if (invalid) { error.textContent = invalid; reason.setAttribute('aria-invalid', 'true'); reason.focus(); return; }
      send.setAttribute('aria-busy', 'true');
      send.textContent = t('account.submissions.withdraw.sending');
      let result;
      try { result = await account.call(`/submissions/${encodeURIComponent(row.id)}/withdrawal`, { method: 'POST', body: { reason: reason.value.trim() } }); } catch { result = { status: 0, body: null }; }
      send.removeAttribute('aria-busy');
      send.textContent = t('account.submissions.withdraw.ask');
      if (result.status !== 200) { error.textContent = withdrawalErrorText(result.status, result.body); (result.status === 422 ? reason : send).focus(); return; }
      box.setOpener(null);
      box.close('confirm');
      openPanel('submissions', { notice: t('account.submissions.withdraw.sent') });
    });
    box.open({ host: dialog?.node.parentElement, opener, focus: reason });
  }
  /* Annulation d'une demande de suppression : sans fenêtre (rien n'est retiré), annoncée dans la liste. */
  async function cancelWithdrawal(row, button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    let result;
    try { result = await account.call(`/submissions/${encodeURIComponent(row.id)}/withdrawal`, { method: 'DELETE' }); } catch { result = { status: 0, body: null }; }
    button.removeAttribute('aria-busy');
    openPanel('submissions', { notice: result.status === 200 ? t('account.submissions.withdraw.cancelled') : withdrawalErrorText(result.status, result.body), tone: result.status === 200 ? 'ok' : 'danger' });
  }
  /* Gestes d'une annonce en vente : « Modifier », « Demander la suppression » ou « Annuler la demande ». */
  function listingActions(row) {
    return [
      row.canEdit ? h('a', { class: 'fs-btn', href: path(row.editHref), 'data-fs-account-edit': row.id, onclick: leavePanel }, t('account.submissions.edit')) : null,
      row.canRequestWithdrawal ? h('button', { type: 'button', class: 'fs-btn fs-btn--link fs-account-danger', 'data-fs-account-withdraw': row.id, onclick: (event) => askWithdrawal(row, event.currentTarget) }, t('account.submissions.withdraw.ask')) : null,
      row.canCancelWithdrawal ? h('button', { type: 'button', class: 'fs-btn fs-btn--link', 'data-fs-account-withdraw-cancel': row.id, onclick: (event) => cancelWithdrawal(row, event.currentTarget) }, t('account.submissions.withdraw.cancel_request')) : null,
    ].filter(Boolean);
  }
  /* État vide du catalogue (fs-ui-surface.css), pointillé : liste vide du dialogue. */
  function emptyState(message, action) {
    return h('div', { class: 'fs-empty fs-empty--dashed' }, h('p', { class: 'fs-empty-text', text: message }), action ? h('div', { class: 'fs-empty-actions' }, action) : '');
  }

  async function openPanel(kind, { notice = '', tone = 'ok' } = {}) {
    const view = ensureDialog();
    view.kind = kind;
    const entries = accountTabs({ purchases: purchasesOn });
    view.tabs.replaceChildren(...entries.map(([key, label]) => h('button', { type: 'button', role: 'tab', class: 'fs-tab', 'aria-selected': String(key === kind), tabindex: key === kind ? '0' : '-1', onclick: () => openPanel(key) }, label)));
    bindTabs(view.tabs); // flèches, Début, Fin (catalogue commun) ; lié une fois
    view.title.textContent = entries.find(([key]) => key === kind)?.[1] || t('account.header.submissions');
    view.body.replaceChildren(h('p', { class: 'fs-account-loading', text: t('account.panel.loading') }));
    showPanel(view);
    const listing = (handle, label) => (handle ? h('a', { class: 'fs-link', href: path(listingPageHref(handle)), text: label }) : null);
    /* Catalogue de l'accueil : ancre de cette page, sinon accueil de la langue de la page. */
    const catalogue = () => h('a', { class: 'fs-btn', href: document.getElementById('fs-catalog') ? '#fs-catalog' : path('#fs-catalog'), onclick: leavePanel }, t('account.panel.see_listings'));
    try {
      if (kind === 'tickets') {
        const { status, body } = await account.call('/tickets/mine');
        if (view.kind !== kind) return;
        if (status === 401) { view.body.replaceChildren(emptyState(t('account.error.expired'))); return; }
        if (status === 503) { view.body.replaceChildren(emptyState(t('account.tickets.off'))); return; }
        if (status !== 200) throw new Error(String(status));
        const rows = ticketRows(body);
        if (!rows.length) { view.body.replaceChildren(emptyState(t('account.tickets.empty'), catalogue())); return; }
        view.body.replaceChildren(h('ul', { class: 'fs-account-list' }, rows.map((row) => h('li', { class: 'fs-row fs-account-row' },
          gameMark(row.game),
          h('div', { class: 'fs-row-main fs-account-row-main' },
            h('p', { class: 'fs-row-meta fs-account-row-meta' }, h('span', { class: 'fs-account-ref', text: row.reference || '—' }), row.gameName ? ` · ${row.gameName}` : '', row.openedAt ? ` · ${t('account.tickets.opened_on', { date: formatDate(row.openedAt) })}` : ''),
            h('p', { class: 'fs-row-title fs-account-row-title', text: row.title || t('account.panel.untitled') })),
          badge(row.label, row.tone),
          h('p', { class: 'fs-row-actions fs-account-row-actions' },
            row.url ? h('a', { class: 'fs-btn fs-btn--primary', href: row.url, target: '_blank', rel: 'noopener noreferrer' }, discordIcon(), `${t('account.tickets.discord')} `, h('span', { 'aria-hidden': 'true', text: '↗' }), external()) : null,
            listing(row.listing, t('account.panel.see_listing')))))));
      } else if (kind === 'purchases') {
        /* « Mes achats » (achat direct) : commande, état du dossier, ticket de livraison dès qu'il existe (ACHAT-15). */
        const { status, body } = await account.call('/purchases/mine');
        if (view.kind !== kind) return;
        if (status === 401) { view.body.replaceChildren(emptyState(t('account.error.expired'))); return; }
        if (status === 503) { view.body.replaceChildren(emptyState(t('account.purchase.off'))); return; }
        if (status !== 200) throw new Error(String(status));
        const rows = purchaseRows(body);
        if (!rows.length) { view.body.replaceChildren(emptyState(t('account.purchase.empty'), catalogue())); return; }
        const note = (row) => (row.status === 'ordered' ? t('account.purchase.note_ordered')
          : row.status === 'paid' && !row.ticketUrl ? t('account.purchase.note_paid') : '');
        view.body.replaceChildren(h('ul', { class: 'fs-account-list' }, rows.map((row) => h('li', { class: 'fs-row fs-account-row', 'data-fs-account-purchase': row.status || 'unknown' },
          gameMark(row.game),
          h('div', { class: 'fs-row-main fs-account-row-main' },
            h('p', { class: 'fs-row-meta fs-account-row-meta' }, row.orderName ? h('span', { class: 'fs-account-ref', text: t('account.purchase.order', { name: row.orderName }) }) : '', row.gameName ? `${row.orderName ? ' · ' : ''}${row.gameName}` : '', row.at ? ` · ${t('account.purchase.on_date', { date: formatDate(row.at) })}` : ''),
            h('p', { class: 'fs-row-title fs-account-row-title', text: row.title || row.reference || t('account.panel.untitled') })),
          badge(row.label, row.tone),
          note(row) ? h('p', { class: 'fs-account-row-note', text: note(row) }) : null,
          h('p', { class: 'fs-row-actions fs-account-row-actions' },
            row.ticketUrl ? h('a', { class: 'fs-btn fs-btn--primary', href: row.ticketUrl, target: '_blank', rel: 'noopener noreferrer' }, discordIcon(), `${t('account.purchase.go')} `, h('span', { 'aria-hidden': 'true', text: '↗' }), external()) : null,
            trophyHref(row.orderName) ? h('a', { class: 'fs-btn', href: path(trophyHref(row.orderName)), 'data-fs-account-trophy': true, onclick: leavePanel }, t('account.purchase.card')) : null,
            listing(row.listing, t('account.panel.see_listing')))))));
      } else {
        const { status, body } = await account.call('/submissions/mine');
        if (view.kind !== kind) return;
        if (status === 401) { view.body.replaceChildren(emptyState(t('account.error.expired'))); return; }
        if (status !== 200) throw new Error(String(status));
        const rows = submissionRows(body);
        const propose = document.querySelector('#sell') ? h('a', { class: 'fs-btn', href: '#sell', 'data-fs-propose-open': true, onclick: leavePanel }, t('account.submissions.propose'))
          : h('a', { class: 'fs-btn', href: path('#sell') }, t('account.submissions.propose'));
        if (!rows.length) { view.body.replaceChildren(emptyState(t('account.submissions.empty'), propose)); return; }
        const date = (iso) => formatDate(iso);
        /* Issue d'un geste (demande de suppression, annulation) : annoncée en tête de liste (zone polie du dialogue). */
        const announced = notice ? h('p', { class: 'fs-alert', 'data-tone': tone === 'danger' ? 'danger' : 'success', role: tone === 'danger' ? 'alert' : 'status', 'data-fs-account-notice': true, text: notice }) : null;
        view.body.replaceChildren(announced || '', h('ul', { class: 'fs-account-list' }, rows.map((row) => {
          const actions = listingActions(row);
          return h('li', { class: `fs-row fs-account-row${row.replaced ? ' fs-account-row--replaced' : ''}`, 'data-fs-account-submission': row.key,
            'data-fs-account-listing-state': row.listingState || null, 'data-fs-account-edit-state': row.edit?.status || null, 'data-fs-account-withdrawal': row.withdrawal?.status || null },
          gameMark(row.game),
          h('div', { class: 'fs-row-main fs-account-row-main' },
            h('p', { class: 'fs-row-meta fs-account-row-meta' }, row.reference ? h('span', { class: 'fs-account-ref', text: row.reference }) : null, [row.gameName, row.replaces ? t('account.submissions.corrected') : ''].filter(Boolean).map((part, index) => (row.reference || index ? ` · ${part}` : part)).join('')),
            h('p', { class: 'fs-row-title fs-account-row-title', text: row.title })),
          badge(row.label, row.tone),
          submissionLine(row, date) ? h('p', { class: 'fs-account-row-note', text: submissionLine(row, date) }) : null,
          listingNotes(row, date).map((note) => h('p', { class: `fs-account-row-note fs-account-row-note--${note.kind}`, 'data-fs-account-note': note.kind }, note.text)),
          row.reason ? rejected(row) : null,
          row.edit?.reason && !row.gone ? editRejected(row) : null,
          row.listing || row.ticketUrl || actions.length ? h('p', { class: 'fs-row-actions fs-account-row-actions' },
            row.ticketUrl ? h('a', { class: 'fs-btn fs-btn--primary', href: row.ticketUrl, target: '_blank', rel: 'noopener noreferrer' }, discordIcon(), `${t('account.submissions.open_ticket')} `, h('span', { 'aria-hidden': 'true', text: '↗' }), external()) : null,
            listing(row.listing, t('account.panel.see_listing')), actions) : null);
        })), h('p', { class: 'fs-account-foot' }, propose));
      }
    } catch {
      if (view.kind === kind) view.body.replaceChildren(emptyState(t('account.error.service')));
    }
  }

  document.addEventListener('fs:account', render);
  render();
  /* Lien « Retrouvez vos annonces » du MP (#my-listings) : ouvre « Mes annonces » ; sans session, d'abord la connexion
     Discord (intention « submissions » gardée dans l'onglet, comme pour les tickets), puis le dialogue au retour.
     Achat direct : #my-purchases (e-mail de commande, ACHAT-15) fait de même pour « Mes achats » (intention « purchases »). */
  const askLogin = (kind = 'submissions') => {
    const view = ensureDialog();
    view.kind = kind;
    view.title.textContent = kind === 'purchases' ? t('account.header.purchases') : t('account.header.submissions');
    view.tabs.replaceChildren();
    view.body.replaceChildren(emptyState(kind === 'purchases' ? t('account.purchase.login') : t('account.submissions.login'),
      h('button', { type: 'button', class: 'fs-btn fs-btn--primary', 'data-fs-account-mine-login': true, onclick: () => { if (!(kind === 'purchases' ? account.login({ kind: 'purchases', game: currentGame() }) : account.login({ kind: 'submissions', game: currentGame() }))) loginButton.title = t('account.error.storage'); } }, discordIcon(), t('account.panel.login'))));
    showPanel(view);
  };
  const hashKind = () => (window.location.hash === '#my-listings' ? 'submissions' : window.location.hash === '#my-purchases' && purchasesOn ? 'purchases' : '');
  const clearHash = () => { try { history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}`); } catch { /* adresse inchangée */ } };
  const openMine = (kind) => (account.session() ? openPanel(kind) : askLogin(kind));
  /* Lien interne vers le fragment sur la même page (« Suivre mon annonce » de l'écran de succès de fs-propose.js) : même
     ouverture, sans rechargement. */
  window.addEventListener('hashchange', () => {
    const kind = hashKind();
    if (!kind) return;
    clearHash();
    openMine(kind);
  });
  const mineHash = hashKind();
  if (mineHash) {
    clearHash();
    if (account.returning) account.returning.promise.then(() => openMine(mineHash)); else openMine(mineHash);
  } else if (account.returning) {
    account.returning.promise.then((outcome) => {
      if (outcome?.ok && outcome.intent?.kind === 'submissions') openPanel('submissions');
      else if (outcome?.ok && outcome.intent?.kind === 'purchases' && purchasesOn) openPanel('purchases');
    });
  }
  return { render, openPanel };
}

/* ------------------------------------------------------------------ démarrage */

if (typeof document !== 'undefined' && !window.FSAccount) {
  const root = document.querySelector('[data-fs-service]');
  const service = serviceBase(root?.getAttribute('data-fs-service'));
  if (service) {
    const account = createAccount(service);
    window.FSAccount = account;
    /* Droits (/me) demandés dès maintenant, sans attendre les modules qui les liront (coque de l'espace équipe, barre de
       l'équipe de la fiche) : ils reçoivent la même promesse (me, une lecture par session). Pendant un retour de Discord,
       rien : la session qui en sortira sera lue. */
    if (!account.returning && account.session()) account.me();
    const mount = (scope) => scope.querySelectorAll('[data-fs-account]').forEach((slot) => mountHeader(account, slot));
    mount(document);
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
  }
}
